import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ExcelDownloadBtn from "components/project/UdExcelDownloadBtn";
import {
  buildEditorSavePayload,
  isEditorFormChanged,
  saveEditorDraft,
} from "components/project/editorDraftUtils";
import PdfDownloadBtn, {
  createPdfReport,
  REPORT_PRINT_PDF_LAYOUT,
} from "components/project/PdfDownloadBtn";
import HmlDownloadBtn from "components/project/HmlDownloadBtn";
import ElectricalSafetyHmlDownloadBtn from "components/project/ElectricalSafetyHmlDownloadBtn";
import ReportTemplateSlide from "components/project/ReportTemplateSlide";
import { downloadElectricalSafetyExcelReport } from "components/project/ElectricalSafetyExcelDownloadBtn";
import { getElectricalSafetyHmlReport } from "lib/api/getElectricalSafetyHmlReport";
import { getImageBase64 } from "lib/api/getImageBase64";
import { postUpdateThermalInfo } from "lib/api/postUpdateThermalInfo";
import {
  getFusionResultTone,
  getImageFusionLabelsByLocale,
  normalizeFusionResultCode,
} from "lib/utils/fusionResult";
import {
  getReportTemplatePref,
  REPORT_TEMPLATE_PREF,
  setReportTemplatePref,
} from "lib/utils/reportTemplatePref";
import {
  getReportPanelScalePref,
  normalizeReportPanelScalePref,
  REPORT_PANEL_SCALE_PREF,
  setReportPanelScalePref,
} from "lib/utils/reportPanelScalePref";
import { useAppStore } from "lib/store/useAppStore";
import ElectricalSafetyReport from "./electricalSafety/electricalSafetyReport";
import ElectricalSafetyTripleSpotModal from "./electricalSafety/components/ElectricalSafetyTripleSpotModal";
import TimeseriesReport from "./timeseries/TimeseriesReport";
import ReportPreparation from "./preparation/Preparation";
import Ud1000ReportTypeImg from "./ud1000/Ud1000ReportTypeImg";

const UD1000_TEMPLATE_ID = REPORT_TEMPLATE_PREF.DEFAULT_TEMPLATE_ID;
const ELECTRICAL_DUTY_TEMPLATE_ID = "electrical-duty-report";
const EQUIPMENT_TIMESERIES_TEMPLATE_ID = "equipment-timeseries-report";
const TEMPLATE_REGISTRATION_ID = "template-registration";

const EMPTY_CIRCLES = [undefined, undefined, undefined];

function hasCompleteClickPoints(candidate) {
  return (
    Array.isArray(candidate?.click_points) &&
    candidate.click_points.length >= 3 &&
    candidate.click_points.slice(0, 3).every((point) => Boolean(point?.value))
  );
}

function getPdfDownloadFileName(file = {}) {
  const rawName =
    file?.file_name || file?.clean_file_name || file?.upload_id || "report";
  const extIndex = rawName.lastIndexOf(".");
  return extIndex > 0 ? rawName.slice(0, extIndex) : rawName;
}

export default function ImageReportPanel({
  editorProps,
  file,
  isReadOnly,
  setIsReadOnly,
  form,
  setForm,
  projectId,
  userId,
  locale,
  reportActionSlot = null,
}) {
  const user = useAppStore((s) => s.user || null);
  const showAlert = useAppStore((s) => s.showAlert);
  const { t, i18n } = useTranslation();
  const canRenderHwp = (i18n.resolvedLanguage ?? i18n.language ?? "")
    .toLowerCase()
    .startsWith("ko");
  const [isSaving, setIsSaving] = useState(false);
  const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false);
  const [imgTemp, setImgTemp] = useState({});
  const [selectedTemplateId, setSelectedTemplateId] = useState(
    REPORT_TEMPLATE_PREF.DEFAULT_TEMPLATE_ID,
  );
  const [reportScale, setReportScale] = useState(
    REPORT_PANEL_SCALE_PREF.DEFAULT_SCALE,
  );
  const [isElectricalTripleSpotModalOpen, setIsElectricalTripleSpotModalOpen] =
    useState(false);
  const [electricalTripleSpotCircles, setElectricalTripleSpotCircles] =
    useState(EMPTY_CIRCLES);
  const [isElectricalTripleSpotSaving, setIsElectricalTripleSpotSaving] =
    useState(false);
  const [pendingElectricalDownloadType, setPendingElectricalDownloadType] =
    useState("");
  const [electricalDownloadPreviewFile, setElectricalDownloadPreviewFile] =
    useState(null);
  const tableRef = useRef();
  const ultrasonicRef = useRef();
  const thermalRef = useRef();
  const fftRef = useRef();
  const uvRef = useRef();
  const electricalThermalCaptureTarget = useRef(null);
  const electricalTripleSpotCaptureTarget = useRef(null);
  const skipNextReportScaleSaveRef = useRef(true);
  const uploadId = file?.upload_id;
  const resolvedUserId = userId ?? user?.id;
  const fileClickPoints = Array.isArray(file?.click_points)
    ? file.click_points
    : EMPTY_CIRCLES;
  const fileClickPoint1 = fileClickPoints[0];
  const fileClickPoint2 = fileClickPoints[1];
  const fileClickPoint3 = fileClickPoints[2];

  const loadReportImages = useCallback(
    async (targetUploadId) => {
      if (!targetUploadId) {
        setImgTemp({});
        return null;
      }

      const { success, data, message } = await getImageBase64(
        targetUploadId,
        "image_report",
      );

      if (!success) {
        console.warn("[ImageReportPanel] Base64 load failed:", message);
        return null;
      }

      const nextImages = {
        ...data,
        image_spectrogram: file?.image_spectrogram,
      };

      setImgTemp(nextImages);
      return nextImages;
    },
    [file?.image_spectrogram],
  );

  useEffect(() => {
    if (!uploadId) {
      setImgTemp({});
      return;
    }

    let cancelled = false;

    (async () => {
      setImgTemp({});
      const nextImages = await loadReportImages(uploadId);
      if (cancelled || !nextImages) return;
    })();

    return () => {
      cancelled = true;
    };
  }, [
    uploadId,
    file?.image_spectrogram,
    file?.image_thermal,
    file?.image_triplespot,
    file?.image_visual,
    loadReportImages,
  ]);

  useEffect(() => {
    setSelectedTemplateId(getReportTemplatePref(resolvedUserId));
  }, [resolvedUserId]);

  useEffect(() => {
    skipNextReportScaleSaveRef.current = true;
    setReportScale(getReportPanelScalePref(resolvedUserId));
  }, [resolvedUserId]);

  useEffect(() => {
    if (!resolvedUserId || selectedTemplateId === TEMPLATE_REGISTRATION_ID) {
      return;
    }

    setReportTemplatePref(resolvedUserId, selectedTemplateId);
  }, [resolvedUserId, selectedTemplateId]);

  useEffect(() => {
    if (skipNextReportScaleSaveRef.current) {
      skipNextReportScaleSaveRef.current = false;
      return;
    }

    if (!resolvedUserId) {
      return;
    }

    setReportPanelScalePref(resolvedUserId, reportScale);
  }, [reportScale, resolvedUserId]);

  useEffect(() => {
    setElectricalTripleSpotCircles([
      fileClickPoint1 ?? undefined,
      fileClickPoint2 ?? undefined,
      fileClickPoint3 ?? undefined,
    ]);
    setPendingElectricalDownloadType("");
    setIsElectricalTripleSpotModalOpen(false);
    setIsElectricalTripleSpotSaving(false);
    setElectricalDownloadPreviewFile(null);
  }, [uploadId, fileClickPoint1, fileClickPoint2, fileClickPoint3]);

  const activeFile = isReadOnly ? form : file;
  const electricalReportFile =
    selectedTemplateId === ELECTRICAL_DUTY_TEMPLATE_ID
      ? (electricalDownloadPreviewFile ?? activeFile)
      : activeFile;
  const fusionLabels = getFusionLabelsByLocale(i18n);
  const fileFusionCode = toFusionCode(
    file?.fusion_result_code ?? file?.fusion_result,
    0,
  );
  const panelFusionCode = toFusionCode(
    electricalReportFile?.fusion_result_code ??
      electricalReportFile?.fusion_result,
    fileFusionCode,
  );
  const panelFusionTone = getFusionResultTone(panelFusionCode);
  const hasDraftChanges = isEditorFormChanged(
    editorProps?.form,
    editorProps?.prevData,
  );
  const closeReportEditor = useCallback(() => {
    setIsDiscardConfirmOpen(false);

    if (typeof setForm === "function") {
      setForm(editorProps?.form ?? file ?? {});
    }
    setIsReadOnly(false);
  }, [editorProps?.form, file, setForm, setIsReadOnly]);
  const requestCloseReportEditor = useCallback(() => {
    if (isSaving) return;

    if (hasDraftChanges) {
      setIsDiscardConfirmOpen(true);
      return;
    }

    closeReportEditor();
  }, [closeReportEditor, hasDraftChanges, isSaving]);

  useEffect(() => {
    if (!isReadOnly && isDiscardConfirmOpen) {
      setIsDiscardConfirmOpen(false);
    }
  }, [isDiscardConfirmOpen, isReadOnly]);

  useEffect(() => {
    if (!isDiscardConfirmOpen) return undefined;

    const handleConfirmKeyDown = (event) => {
      if (event.key !== "Escape") return;

      event.preventDefault();
      event.stopImmediatePropagation();
      setIsDiscardConfirmOpen(false);
    };

    window.addEventListener("keydown", handleConfirmKeyDown, true);
    return () => {
      window.removeEventListener("keydown", handleConfirmKeyDown, true);
    };
  }, [isDiscardConfirmOpen]);
  const shouldShowDownloadActions =
    selectedTemplateId !== EQUIPMENT_TIMESERIES_TEMPLATE_ID;
  const shouldShowScaleControls =
    selectedTemplateId === UD1000_TEMPLATE_ID ||
    selectedTemplateId === ELECTRICAL_DUTY_TEMPLATE_ID;
  const shouldShowActionBar =
    selectedTemplateId !== TEMPLATE_REGISTRATION_ID &&
    selectedTemplateId !== EQUIPMENT_TIMESERIES_TEMPLATE_ID &&
    (shouldShowDownloadActions || shouldShowScaleControls || reportActionSlot);
  const electricalSafetyDownloadFileName =
    electricalReportFile?.clean_file_name ??
    file?.clean_file_name ??
    electricalReportFile?.file_name ??
    file?.file_name;

  useEffect(() => {
    if (
      selectedTemplateId !== ELECTRICAL_DUTY_TEMPLATE_ID ||
      !pendingElectricalDownloadType ||
      isElectricalTripleSpotModalOpen ||
      !hasCompleteClickPoints(electricalReportFile)
    ) {
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        await new Promise((resolve) => {
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(resolve);
          });
        });

        if (cancelled) return;

        if (pendingElectricalDownloadType === "pdf") {
          await createPdfReport(
            tableRef,
            getPdfDownloadFileName(file),
            REPORT_PRINT_PDF_LAYOUT.single,
          );
        } else if (pendingElectricalDownloadType === "excel") {
          await downloadElectricalSafetyExcelReport({
            file: electricalReportFile,
            images: imgTemp,
            user,
          });
        } else if (pendingElectricalDownloadType === "hml") {
          const result = await getElectricalSafetyHmlReport(
            electricalReportFile?.upload_id ?? file?.upload_id,
            electricalSafetyDownloadFileName,
          );

          if (!result?.success) {
            showAlert({
              text:
                result.message ||
                "Failed to download electrical safety HML report.",
              messageKey: result.messageKey,
              messageParams: result.messageParams,
              success: false,
              autoClose: true,
            });
          }
        }
      } catch (error) {
        console.error(
          "[ImageReportPanel] electrical report download failed",
          error,
        );
        showAlert({
          text: t(
            "imageFileIndex.electricalSafety.downloadModal.downloadFailed",
            {
              defaultValue: "Failed to generate the report. Please try again.",
            },
          ),
          success: false,
          autoClose: true,
        });
      } finally {
        if (!cancelled) {
          setPendingElectricalDownloadType("");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    electricalReportFile,
    electricalSafetyDownloadFileName,
    file,
    imgTemp,
    isElectricalTripleSpotModalOpen,
    pendingElectricalDownloadType,
    selectedTemplateId,
    showAlert,
    t,
    user,
  ]);

  return (
    <>
      <ReportTemplateSlide
        selectedId={selectedTemplateId}
        onSelect={(item) => setSelectedTemplateId(item.id)}
        onCreateTemplate={() => setSelectedTemplateId(TEMPLATE_REGISTRATION_ID)}
      />
      <div
        className={`reportPanel ${
          panelFusionTone === "warning"
            ? "active1"
            : panelFusionTone === "abnormal"
              ? "active2"
              : panelFusionTone === "recheck"
                ? "active3"
                : ""
        }`}
      >
        {shouldShowActionBar ? (
          <div className="reportPanelActionBar">
            {shouldShowDownloadActions ? (
              <div className="reportPanelDownloadActions">
                {renderPdfDownloadButton()}
                {renderExcelDownloadButton()}
                {canRenderHwp ? renderElectricalSafetyHmlDownloadButton() : null}
                {canRenderHwp &&
                  selectedTemplateId !== TEMPLATE_REGISTRATION_ID &&
                  selectedTemplateId !== EQUIPMENT_TIMESERIES_TEMPLATE_ID &&
                  selectedTemplateId !== ELECTRICAL_DUTY_TEMPLATE_ID && (
                    <HmlDownloadBtn
                      t={t("imageFileIndex.buttons.HmlDownload")}
                      upload_id={file?.upload_id}
                      file_name={file?.clean_file_name}
                      file_type={file?.file_type}
                    />
                  )}
              </div>
            ) : null}
            {shouldShowScaleControls || reportActionSlot ? (
              <div className="reportPanelEditActions">
                {renderReportScaleControls()}
                {reportActionSlot}
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="reportPanelContent" ref={tableRef}>
          {renderSelectedReport()}
        </div>
        {isElectricalTripleSpotModalOpen ? (
          <ElectricalSafetyTripleSpotModal
            file={electricalReportFile}
            circles={electricalTripleSpotCircles}
            setCircles={setElectricalTripleSpotCircles}
            thermalCaptureTarget={electricalThermalCaptureTarget}
            tripleSpotCaptureTarget={electricalTripleSpotCaptureTarget}
            currentPaletteOptionNumber={
              electricalReportFile?.thermal_palette ?? 0
            }
            imgChangeLoading={false}
            isSaving={isElectricalTripleSpotSaving}
            onClose={() => {
              if (isElectricalTripleSpotSaving) return;
              setIsElectricalTripleSpotModalOpen(false);
              setPendingElectricalDownloadType("");
            }}
            onSave={handleSaveElectricalTripleSpot}
          />
        ) : null}
        {isDiscardConfirmOpen ? (
          <div
            className="reportEditDiscardConfirmOverlay"
            onClick={() => setIsDiscardConfirmOpen(false)}
            role="presentation"
          >
            <section
              aria-describedby="report-edit-discard-description"
              aria-labelledby="report-edit-discard-title"
              aria-modal="true"
              className="reportEditDiscardConfirm"
              onClick={(event) => event.stopPropagation()}
              role="alertdialog"
            >
              <div className="reportEditDiscardConfirmBody">
                <span aria-hidden="true">!</span>
                <div>
                  <h3 id="report-edit-discard-title">
                    보고서 수정을 종료할까요?
                  </h3>
                  <p id="report-edit-discard-description">
                    변경사항이 저장되지 않습니다. 정말로 팝업창을
                    닫으시겠습니까?
                  </p>
                </div>
              </div>
              <div className="reportEditDiscardConfirmActions">
                <button
                  className="keepEditingBtn"
                  onClick={() => setIsDiscardConfirmOpen(false)}
                  type="button"
                >
                  계속 수정
                </button>
                <button
                  className="discardEditBtn"
                  onClick={closeReportEditor}
                  type="button"
                >
                  저장하지 않고 닫기
                </button>
              </div>
            </section>
          </div>
        ) : null}
      </div>
    </>
  );

  function toFusionCode(value, fallback = 0) {
    return normalizeFusionResultCode(value, fallback);
  }

  function handleChange(e) {
    const { id, value } = e.target;
    setForm((prev) => ({
      ...(prev ?? {}),
      [id]: value,
    }));
  }

  function handleFusionResultChange(e) {
    const nextCode = toFusionCode(e.target.value, 0);
    setForm((prev) => ({
      ...(prev ?? {}),
      fusion_result: nextCode,
      fusion_result_code: nextCode,
    }));
  }

  async function handleSaveReport() {
    if (!editorProps?.prevData || isSaving) return;

    setIsSaving(true);
    await saveEditorDraft(buildEditorSavePayload(editorProps, t));
    setIsSaving(false);
  }

  function openElectricalTripleSpotModal(downloadType) {
    setElectricalTripleSpotCircles([
      electricalReportFile?.click_points?.[0] ?? undefined,
      electricalReportFile?.click_points?.[1] ?? undefined,
      electricalReportFile?.click_points?.[2] ?? undefined,
    ]);
    setPendingElectricalDownloadType(downloadType);
    setIsElectricalTripleSpotModalOpen(true);
  }

  async function handleSaveElectricalTripleSpot(saveOptions = {}) {
    if (
      isElectricalTripleSpotSaving ||
      !electricalTripleSpotCircles.every((circle) => Boolean(circle))
    ) {
      return;
    }

    setIsElectricalTripleSpotSaving(true);
    const resolvedLocale =
      locale ?? i18n.resolvedLanguage ?? i18n.language ?? "ko-KR";
    const requestedCreateInspectionResult =
      saveOptions &&
      typeof saveOptions === "object" &&
      saveOptions.createInspectionResult === true;

    try {
      const response = await postUpdateThermalInfo({
        refs: [
          electricalThermalCaptureTarget,
          electricalTripleSpotCaptureTarget,
        ],
        project_id: projectId ?? electricalReportFile?.project_id,
        upload_id: electricalReportFile?.upload_id,
        click_points: electricalTripleSpotCircles,
        create_inspection_result:
          electricalTripleSpotCircles.filter(Boolean).length >= 3 &&
          requestedCreateInspectionResult,
        currentPaletteOptionNumber: electricalReportFile?.thermal_palette ?? 0,
        volume: electricalReportFile?.volume,
        avg_decibel: electricalReportFile?.avg_decibel,
        inspection_result: electricalReportFile?.inspection_result,
        fusion_result:
          electricalReportFile?.fusion_result_code ??
          electricalReportFile?.fusion_result,
        locale: resolvedLocale,
      });

      showAlert({
        text: response?.message,
        messageKey: response?.messageKey,
        messageParams: response?.messageParams,
        success: response?.success,
        autoClose: true,
      });

      if (!response?.success) {
        return;
      }

      const nextPreviewFile = {
        ...(electricalReportFile ?? {}),
        click_points: [...electricalTripleSpotCircles],
      };

      setElectricalDownloadPreviewFile(nextPreviewFile);
      setForm((prev) => ({
        ...(prev ?? {}),
        click_points: [...electricalTripleSpotCircles],
      }));
      await loadReportImages(electricalReportFile?.upload_id);
      setIsElectricalTripleSpotModalOpen(false);

      if (typeof editorProps?.refetch === "function") {
        editorProps.refetch(electricalReportFile?.upload_id);
      }
    } catch (error) {
      console.error(
        "[ImageReportPanel] save electrical triple spot failed",
        error,
      );
      showAlert({
        textKey: "api.common.networkError",
        success: false,
        autoClose: true,
      });
    } finally {
      setIsElectricalTripleSpotSaving(false);
    }
  }

  function renderSelectedReport() {
    if (selectedTemplateId === ELECTRICAL_DUTY_TEMPLATE_ID) {
      return (
        <ElectricalSafetyReport
          file={electricalReportFile}
          images={imgTemp}
          user={user}
          onRequireTripleSpot={() => openElectricalTripleSpotModal("")}
          previewScale={reportScale}
          isReadOnly={isReadOnly}
          handleChange={handleChange}
          onSave={handleSaveReport}
          onCancel={requestCloseReportEditor}
          isSaving={isSaving}
          hasChanges={hasDraftChanges}
        />
      );
    }

    if (selectedTemplateId === EQUIPMENT_TIMESERIES_TEMPLATE_ID) {
      return (
        <TimeseriesReport
          file={activeFile}
          sourceFile={file}
          projectId={projectId ?? file?.project_id}
          userId={userId ?? user?.id}
          locale={locale ?? i18n.resolvedLanguage ?? i18n.language}
          reportActionSlot={reportActionSlot}
          isReadOnly={isReadOnly}
          handleChange={handleChange}
          onSave={handleSaveReport}
          onCancel={requestCloseReportEditor}
          isSaving={isSaving}
          hasChanges={hasDraftChanges}
        />
      );
    }

    if (selectedTemplateId === TEMPLATE_REGISTRATION_ID) {
      return <ReportPreparation />;
    }

    return (
      <Ud1000ReportTypeImg
        file={file}
        isReadOnly={isReadOnly}
        form={form}
        t={t}
        imgTemp={imgTemp}
        previewScale={reportScale}
        fusionLabels={fusionLabels}
        fileFusionCode={fileFusionCode}
        toFusionCode={toFusionCode}
        handleChange={handleChange}
        handleFusionResultChange={handleFusionResultChange}
        ultrasonicRef={ultrasonicRef}
        thermalRef={thermalRef}
        fftRef={fftRef}
        uvRef={uvRef}
        setIsReadOnly={requestCloseReportEditor}
        onSave={handleSaveReport}
        isSaving={isSaving}
        hasChanges={hasDraftChanges}
      />
    );
  }

  function renderPdfDownloadButton() {
    if (
      selectedTemplateId === TEMPLATE_REGISTRATION_ID ||
      selectedTemplateId === EQUIPMENT_TIMESERIES_TEMPLATE_ID
    ) {
      return null;
    }

    if (selectedTemplateId === ELECTRICAL_DUTY_TEMPLATE_ID) {
      return (
        <button
          type="button"
          className="btn-bg-blue"
          onClick={() => {
            if (hasCompleteClickPoints(electricalReportFile)) {
              setPendingElectricalDownloadType("pdf");
              return;
            }

            openElectricalTripleSpotModal("pdf");
          }}
        >
          {t("imageFileIndex.buttons.PdfDownload")}
        </button>
      );
    }

    return (
      <PdfDownloadBtn
        t={t("imageFileIndex.buttons.PdfDownload")}
        ref={tableRef}
        file_name={file?.file_name}
        layout={REPORT_PRINT_PDF_LAYOUT.single}
      />
    );
  }

  function renderExcelDownloadButton() {
    if (selectedTemplateId === ELECTRICAL_DUTY_TEMPLATE_ID) {
      return (
        <button
          type="button"
          className="btn-bg-blue"
          onClick={() => {
            if (hasCompleteClickPoints(electricalReportFile)) {
              setPendingElectricalDownloadType("excel");
              return;
            }

            openElectricalTripleSpotModal("excel");
          }}
        >
          {t("imageFileIndex.buttons.ExcelDownload")}
        </button>
      );
    }

    if (
      selectedTemplateId === EQUIPMENT_TIMESERIES_TEMPLATE_ID ||
      selectedTemplateId === TEMPLATE_REGISTRATION_ID
    ) {
      return null;
    }

    return <ExcelDownloadBtn t={t} images={imgTemp} form={file} />;
  }

  function renderElectricalSafetyHmlDownloadButton() {
    if (selectedTemplateId !== ELECTRICAL_DUTY_TEMPLATE_ID) {
      return null;
    }

    return (
      <ElectricalSafetyHmlDownloadBtn
        label={t("imageFileIndex.buttons.HmlDownload")}
        uploadId={electricalReportFile?.upload_id ?? file?.upload_id}
        fileName={electricalSafetyDownloadFileName}
        onClick={() => {
          if (hasCompleteClickPoints(electricalReportFile)) {
            setPendingElectricalDownloadType("hml");
            return;
          }

          openElectricalTripleSpotModal("hml");
        }}
      />
    );
  }

  function handleAdjustReportScale(direction) {
    const delta = direction * REPORT_PANEL_SCALE_PREF.STEP;
    setReportScale((prev) => normalizeReportPanelScalePref(prev + delta));
  }

  function renderReportScaleControls() {
    if (!shouldShowScaleControls) {
      return null;
    }

    const isDecreaseDisabled =
      reportScale <= REPORT_PANEL_SCALE_PREF.MIN_SCALE + 0.001;
    const isIncreaseDisabled =
      reportScale >= REPORT_PANEL_SCALE_PREF.MAX_SCALE - 0.001;

    return (
      <div className="reportPanelScaleControls">
        <span className="reportPanelScaleLabel">
          {t("imageFileIndex.buttons.reportScale", {
            defaultValue: "Size",
          })}
        </span>
        <button
          type="button"
          className="reportPanelScaleButton"
          onClick={() => handleAdjustReportScale(-1)}
          disabled={isDecreaseDisabled}
          aria-label={t("imageFileIndex.buttons.reportScaleDown", {
            defaultValue: "Decrease report size",
          })}
        >
          -
        </button>
        <span className="reportPanelScaleValue">
          {Math.round(reportScale * 100)}%
        </span>
        <button
          type="button"
          className="reportPanelScaleButton"
          onClick={() => handleAdjustReportScale(1)}
          disabled={isIncreaseDisabled}
          aria-label={t("imageFileIndex.buttons.reportScaleUp", {
            defaultValue: "Increase report size",
          })}
        >
          +
        </button>
      </div>
    );
  }
}

const getFusionLabelsByLocale = (i18n) => {
  return getImageFusionLabelsByLocale(i18n);
};
